// Utility functions for calculating date-aware table statuses
import { reservationApi } from '../services/reservationApi';
import type { RestaurantTable, ReserveTableDTO } from '../types/reservations';

/**
 * Calculate date-aware table statuses based on current reservations
 * @param tables Array of restaurant tables
 * @param targetDate Optional date to calculate status for (defaults to today)
 * @returns Promise<RestaurantTable[]> Tables with updated statuses
 */
export const calculateDateAwareTableStatuses = async (
  tables: RestaurantTable[], 
  targetDate?: string
): Promise<RestaurantTable[]> => {
  try {
    const today = targetDate || new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Get reservations for the target date
    const dayReservations = await reservationApi.searchReservationsByPeriodWithParams(today, 'today');
    
    // Create a map of table IDs to their reservation status
    const tableReservationMap = new Map<number, { status: string; reservation: ReserveTableDTO }>();
    
    dayReservations.forEach(reservation => {
      if (reservation.restaurantTableId && Array.isArray(reservation.restaurantTableId)) {
        reservation.restaurantTableId.forEach(table => {
          const tableId = table.id;
          let status = 'Available';
          
          // Determine status based on reservation status and time
          if (reservation.status === 'Accepted') {
            const reservationTime = new Date(`${reservation.date}T${reservation.time}`);
            const now = new Date();
            const timeDiff = now.getTime() - reservationTime.getTime();
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            
            // If reservation time has passed by more than 2 hours, consider it occupied
            // If reservation is within 2 hours or in future, consider it reserved
            status = hoursDiff > 2 ? 'Occupied' : 'Reserved';
          }
          
          tableReservationMap.set(tableId, { status, reservation });
        });
      }
    });
    
    // Update table statuses based on reservations
    const updatedTables = tables.map(table => {
      const reservationInfo = tableReservationMap.get(table.id);
      return {
        ...table,
        status: (reservationInfo?.status as typeof table.status) || 'Available',
        // Store reservation info if needed
        currentReservation: reservationInfo?.reservation
      } as RestaurantTable;
    });
    
    return updatedTables;
  } catch (error) {
    console.error('Error calculating table statuses:', error);
    return tables; // Return original data on error
  }
};

/**
 * Get status color classes for table status badges
 * @param status Table status
 * @returns CSS classes string
 */
export const getTableStatusColor = (status: string): string => {
  switch (status) {
    case 'Available':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'Occupied':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    case 'Reserved':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'Maintenance':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

/**
 * Get status color for floor plan rendering
 * @param status Table status
 * @returns Hex color string
 */
export const getFloorPlanStatusColor = (status: string): string => {
  switch (status) {
    case 'available': return '#7bf1a8'; // Green
    case 'occupied': return '#ffa2a2'; // Red
    case 'reserved': return '#ffb86a'; // Orange
    case 'maintenance': return '#6b7280'; // Gray
    default: return '#7bf1a8'; // Default to available (green)
  }
};